import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { addDays, dayOfWeek, endOfMonth, startOfMonth, startOfWeek, todayIn } from '@tt/shared';
import { Alert, Spinner } from '@/components/ui/misc';
import { describeError } from '@/lib/errors';
import { formatDate, formatDayNumber, formatHours, formatMinutes, formatMonth } from '@/lib/format';
import { useMe, useMonth } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { useTimesheetLock } from '@/lib/use-timesheet-lock';
import { cn } from '@/lib/utils';
import { TimeNav } from './time-nav';

/** FR2 month calendar: total hours per day; a day opens the day view. */
export function MonthView() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { data: me } = useMe();
  const { month } = useParams({ from: '/app/time/month/$month' });
  const query = useMonth(month);
  const lock = useTimesheetLock();
  const weekStartDay = me?.organization.weekStartDay ?? 1;
  const today = todayIn(me?.timeZone ?? 'UTC');

  const first = startOfMonth(month);
  const last = endOfMonth(month);
  const gridStart = startOfWeek(first, weekStartDay);
  const cells: string[] = [];
  for (let d = gridStart; d <= last || cells.length % 7 !== 0; d = addDays(d, 1)) cells.push(d);
  const weeks: string[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  const byDate = new Map((query.data?.days ?? []).map((d) => [d.date, d]));
  const maxMinutes = Math.max(1, ...(query.data?.days ?? []).map((d) => d.minutes));
  const headers = Array.from({ length: 7 }, (_, i) => (weekStartDay + i) % 7);

  return (
    <div>
      <TimeNav
        view="month"
        anchor={month}
        title={formatMonth(month, fmt)}
        total={formatHours(query.data?.monthTotal ?? 0, fmt)}
      />
      {query.isPending ? <Spinner /> : null}
      {query.isError ? <Alert variant="error">{describeError(query.error, t)}</Alert> : null}
      {query.data ? (
        <table className="w-full table-fixed border-collapse rounded-lg border bg-background text-sm">
          <caption className="sr-only">{t('time.monthView')}</caption>
          <thead>
            <tr>
              {headers.map((dow) => (
                <th
                  key={dow}
                  scope="col"
                  className="border-b px-1 py-2 text-center text-xs font-medium uppercase text-muted-foreground"
                >
                  <span className="sm:hidden">{t(`weekdays.${dow}`).slice(0, 2)}</span>
                  <span className="hidden sm:inline">{t(`weekdays.${dow}`)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week[0]}>
                {week.map((day) => {
                  const info = byDate.get(day);
                  const inMonth = day >= first && day <= last;
                  const weekend = dayOfWeek(day) === 0 || dayOfWeek(day) === 6;
                  const locked = lock.isLocked(day);
                  // Light tints only so the dark value text keeps ≥ 4.5:1 contrast (WCAG 1.4.3).
                  const intensity = info ? 0.08 + 0.3 * (info.minutes / maxMinutes) : 0;
                  const label = info
                    ? t('time.hoursOnDay', {
                        hours: formatHours(info.minutes, fmt),
                        date: formatDate(day, fmt, { dateStyle: 'full' }),
                      })
                    : t('time.noHours', { date: formatDate(day, fmt, { dateStyle: 'full' }) });
                  return (
                    <td
                      key={day}
                      className={cn(
                        'h-16 border p-0 align-top sm:h-20',
                        !inMonth && 'bg-muted/30',
                        weekend && inMonth && 'bg-muted/10',
                        locked && 'bg-muted/50',
                      )}
                    >
                      <Link
                        to="/time/day/$date"
                        params={{ date: day }}
                        className={cn(
                          'flex h-full w-full flex-col p-1 hover:bg-accent sm:p-2',
                          !inMonth && 'text-muted-foreground',
                        )}
                        // A grey day says "closed" only to someone who can see the grey, so the
                        // accessible name carries it too (WCAG 1.4.1). The day still opens: a
                        // closed period hides nothing, it only refuses changes.
                        aria-label={locked ? t('time.closedLabel', { label }) : label}
                      >
                        <span
                          className={cn(
                            'self-start text-xs tabular-nums',
                            day === today &&
                              'rounded-full bg-primary px-1.5 font-semibold text-primary-foreground',
                          )}
                        >
                          {formatDayNumber(day, fmt)}
                        </span>
                        {info ? (
                          <span
                            className="mt-auto self-end rounded px-1 text-sm font-semibold tabular-nums"
                            style={{ backgroundColor: `hsl(221 83% 45% / ${intensity})` }}
                          >
                            {formatMinutes(info.minutes, fmt)}
                          </span>
                        ) : null}
                      </Link>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
